import { CheckoutEventNames, type PaddleEventData } from "@paddle/paddle-js"
import { useNavigate } from "@solidjs/router"
import { type Accessor, createSignal } from "solid-js"
import { MainModule } from "../../features/main-module"
import { type OfferingProduct } from "../../features/payment/domain/models/offering"
import { OfferingType } from "../../features/payment/domain/models/offering-type"
import { AppRoutes } from "../../shared/app-routes"
import { OfferConfig } from "../../shared/offer-config"
import { useCart, type CartContextType } from "../../shared/providers/cart.provider"
import { usePaddle } from "../../shared/providers/paddle.provider"
import { useProducts } from "../../shared/providers/products.provider"
import { useTracking } from "../../shared/providers/tracking.provider"
import { redirectTo } from "../../shared/helpers/redirect-to"
import { buildCheckoutCustomData } from "../../shared/helpers/checkout-custom-data"
import { waitForLandingIp } from "../../shared/helpers/landing-touch"
import { getFacebookCookies, getIpAddress } from "../../shared/helpers/meta-ads-utils"
import { buildMetaPurchaseCustomData } from "../../shared/helpers/meta-ads/meta-purchase-custom-data"
import { getMetaTestEventCode } from "../../shared/helpers/meta-ads/meta-test-event-code"
import { trackPixelInitiateCheckout, trackPixelPurchase } from "../../shared/helpers/meta-pixel"
import { environment, MetaAdsPixelId } from "../../config"
import * as Sentry from "@sentry/browser"

export type WebinarVariantRoute = AppRoutes.Webinar | AppRoutes.WebinarB

export interface WebinarCheckout {
  readonly product: OfferingProduct | undefined
  readonly showCheckout: () => Promise<void>
  readonly checkoutUnavailable: Accessor<boolean>
  readonly markCheckoutUnavailable: () => void
}

const PURCHASE_FOLLOW_UP_DELAY_MS = 1000
const LANDING_IP_WAIT_MS = 1000
const MS_PER_SECOND = 1000

const PAYMENT_FAILURE_EVENTS: ReadonlySet<string> = new Set<string>([
  CheckoutEventNames.CHECKOUT_ERROR,
  CheckoutEventNames.CHECKOUT_FAILED,
  CheckoutEventNames.CHECKOUT_PAYMENT_FAILED,
  CheckoutEventNames.CHECKOUT_PAYMENT_ERROR,
])

const paymentFailureReason = (data: PaddleEventData): string =>
  data.code ?? data.detail ?? data.type ?? data.name ?? "unknown"

const sendMetaPurchaseEvent = async (data: PaddleEventData): Promise<void> => {
  try {
    const { fbc, fbp } = getFacebookCookies()
    const ipAddress = await getIpAddress()
    await MainModule.getPaymentComponent().provideMetaAds().execute({
      pixelId: MetaAdsPixelId,
      eventName: 'Purchase',
      eventTime: Math.floor(Date.now() / MS_PER_SECOND),
      eventId: data.data?.transaction_id,
      email: data.data?.customer?.email,
      fbc,
      fbp,
      ipAddress,
      userAgent: navigator.userAgent,
      customData: buildMetaPurchaseCustomData(data.data),
      testEventCode: getMetaTestEventCode(environment),
      eventSourceUrl: window.location.href
    })
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        context: "Failed to send Meta Ads event on checkout completion",
      },
    })
    console.error("Failed to send Meta Ads event:", error)
  }
}

const addPurchasesToCart = async (
  customerId: string,
  transactionId: string,
  addProductToCart: CartContextType["addProductToCart"]
): Promise<void> => {
  const getSubscriptions = MainModule.getPaymentComponent().provideGetSubscriptions()
  const subscriptions = await getSubscriptions.execute(customerId, transactionId)
  addProductToCart(OfferingType.BaseSubscription, subscriptions[0].id)
  addProductToCart(OfferingType.Pro, "")
}

export function useWebinarCheckout(variantRoute: WebinarVariantRoute): WebinarCheckout {
  const navigate = useNavigate()
  const { paddle, ensurePaddle } = usePaddle()
  const {
    trackCheckoutOpened,
    trackCheckoutAbandoned,
    trackCheckoutPaymentFailed,
    trackCheckoutCompleted,
  } = useTracking()
  const { products } = useProducts()
  const { addProductToCart } = useCart()
  const [checkoutUnavailable, setCheckoutUnavailable] = createSignal(false)
  let checkoutOpening = false

  const product = products()[OfferingType.Pro]

  const completePurchase = async (data: PaddleEventData): Promise<void> => {
    const customerId = data.data?.customer?.id
    const transactionId = data.data?.transaction_id
    window.fpr("referral", { email: data.data?.customer?.email, uid: customerId })
    trackPixelPurchase(data.data)
    await sendMetaPurchaseEvent(data)
    paddle()?.Checkout.close()
    try {
      window.plausible("webinar-purchased")
    } catch {}

    if (!customerId || !transactionId) return
    await addPurchasesToCart(customerId, transactionId, addProductToCart)
    redirectTo(AppRoutes.Thanks, navigate)
  }

  const createCheckoutEventHandler = (purchased: OfferingProduct): ((data: PaddleEventData) => void) => {
    let checkoutCompleted = false
    return (data) => {
      if (data.name === CheckoutEventNames.CHECKOUT_COMPLETED) {
        checkoutCompleted = true
        trackCheckoutCompleted(purchased, {
          email: data.data?.customer?.email,
          paddleCustomerId: data.data?.customer?.id,
        })
        setTimeout(() => {
          void completePurchase(data)
        }, PURCHASE_FOLLOW_UP_DELAY_MS)
      } else if (data.name === CheckoutEventNames.CHECKOUT_CLOSED) {
        if (!checkoutCompleted) trackCheckoutAbandoned(purchased)
      } else if (data.name && PAYMENT_FAILURE_EVENTS.has(data.name)) {
        trackCheckoutPaymentFailed(purchased, paymentFailureReason(data))
      }
    }
  }

  const showCheckout = async (): Promise<void> => {
    if (checkoutOpening) return
    if (!product?.id) {
      setCheckoutUnavailable(true)
      return
    }

    checkoutOpening = true
    try {
      trackCheckoutOpened(product)
      trackPixelInitiateCheckout(product)
      const [instance] = await Promise.all([ensurePaddle({ reportFailure: true }), waitForLandingIp(LANDING_IP_WAIT_MS)])
      if (!instance) {
        setCheckoutUnavailable(true)
        return
      }

      instance.Update({ eventCallback: createCheckoutEventHandler(product) })
      instance.Checkout.open({
        items: [{ priceId: product.id, quantity: 1 }],
        discountId: OfferConfig.discountIds[variantRoute],
        customData: buildCheckoutCustomData(),
      })
    } finally {
      checkoutOpening = false
    }
  }

  return {
    product,
    showCheckout,
    checkoutUnavailable,
    markCheckoutUnavailable: () => setCheckoutUnavailable(true),
  }
}
